import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { EventApiService } from '../../../core/api-services/event-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { Scholarship } from '../../../core/models/scholarship';
import {
  CreateEvent,
  EventAttendanceImportResult,
  EventAttendanceRow,
  EventDeliveryMode,
  EventDetail,
  EventRegistrationStatus,
  EventRegistrationSummary,
  EventReportRow,
  EventSessionInput,
  EventStatus,
  EventStatusAction,
  EventSummary,
  EventTargetAudience,
  EventType,
} from '../../../core/models/event';

/**
 * Admin events console.
 *
 * Create an event for a target audience, publish it, confirm the signups (bulk
 * approve or reject with a reason), track attendance on the day — marking people
 * or importing the attendance sheet — and close the event to produce the
 * attendee register.
 */
@Component({
  selector: 'app-admin-events',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './events.html',
  styleUrl: './events.scss',
})
export class AdminEventsComponent implements OnInit {
  private readonly api = inject(EventApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly fb = inject(FormBuilder);

  readonly events = signal<EventSummary[]>([]);
  readonly selected = signal<EventDetail | null>(null);
  readonly registrations = signal<EventRegistrationSummary[]>([]);
  readonly attendance = signal<EventAttendanceRow[]>([]);
  readonly report = signal<EventReportRow[]>([]);
  readonly scholarships = signal<Scholarship[]>([]);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly statusFilter = signal<EventStatus | ''>('');
  readonly search = signal('');
  readonly audienceFilter = signal<EventTargetAudience | ''>('');

  /**
   * Which slice of the calendar the console is showing. "Past" is the
   * reconciliation view: finished events plus any published event whose date has
   * passed but which nobody has closed yet.
   */
  readonly scope = signal<'upcoming' | 'past' | 'all'>('upcoming');
  readonly chosen = signal<Set<number>>(new Set<number>());
  readonly showForm = signal(false);
  readonly editingId = signal<number | null>(null);
  readonly tab = signal<'registrations' | 'attendance' | 'report'>('registrations');
  readonly rejectReason = signal('');
  readonly correspondence = this.fb.group({
    audience: ['CONFIRMED' as 'CONFIRMED' | 'ATTENDEES' | 'NO_SHOWS'],
    subject: ['', Validators.required],
    body: ['', Validators.required],
  });
  readonly importResult = signal<EventAttendanceImportResult | null>(null);

  readonly eventTypes: EventType[] = ['ORIENTATION', 'WORKSHOP', 'WEBINAR', 'NETWORKING', 'OTHER'];
  readonly deliveryModes: EventDeliveryMode[] = ['IN_PERSON', 'ONLINE', 'HYBRID'];
  readonly audiences: EventTargetAudience[] = ['PUBLIC', 'ALL_AWARDEES', 'SCHOLARSHIP_AWARDEES'];
  readonly statuses: EventStatus[] = ['DRAFT', 'PUBLISHED', 'CLOSED', 'CANCELLED'];

  readonly form = this.fb.group({
    title: ['', Validators.required],
    description: [''],
    eventType: ['OTHER' as EventType],
    deliveryMode: ['IN_PERSON' as EventDeliveryMode],
    targetAudience: ['PUBLIC' as EventTargetAudience],
    scholarshipId: [null as number | null],
    isMandatory: [false],
    cycle: [''],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    registrationOpenDate: [''],
    registrationCloseDate: [''],
    location: [''],
    meetingLink: [''],
    allowPublicSignup: [false],
    capacity: [null as number | null],
    notes: [''],
    reminderHoursBefore: [24],
    publish: [true],
    sessions: this.fb.array([]),
  });

  get sessions(): FormArray {
    return this.form.get('sessions') as FormArray;
  }

  ngOnInit(): void {
    this.load();
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => undefined,
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');

    const scope = this.scope();
    const past = scope === 'all' ? null : scope === 'past';

    this.api
      .getEventsForAdmin(
        this.statusFilter() || null,
        this.audienceFilter() || null,
        this.search() || undefined,
        past
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (events) => this.events.set(events ?? []),
        error: () => this.error.set('The events could not be loaded.'),
      });
  }

  selectScope(scope: 'upcoming' | 'past' | 'all'): void {
    if (this.scope() === scope) {
      return;
    }

    this.scope.set(scope);
    this.load();
  }

  // ---- Create and edit ---------------------------------------------------

  addSession(): void {
    this.sessions.push(this.buildSession());
  }

  removeSession(index: number): void {
    this.sessions.removeAt(index);
  }

  openCreate(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showForm.set(true);
  }

  openEdit(event: EventSummary): void {
    this.busy.set(true);
    this.api
      .getEventForAdmin(event.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.editingId.set(detail.id);
          this.resetForm(detail);
          this.showForm.set(true);
        },
        error: () => this.error.set('The event could not be opened.'),
      });
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
  }

  save(): void {
    if (this.form.invalid || this.busy()) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const request: CreateEvent = {
      title: value.title ?? '',
      description: value.description || undefined,
      eventType: (value.eventType ?? 'OTHER') as EventType,
      deliveryMode: (value.deliveryMode ?? 'IN_PERSON') as EventDeliveryMode,
      targetAudience: (value.targetAudience ?? 'PUBLIC') as EventTargetAudience,
      scholarshipId: value.scholarshipId ?? null,
      isMandatory: !!value.isMandatory,
      cycle: value.cycle || undefined,
      startDate: value.startDate ?? '',
      endDate: value.endDate ?? '',
      registrationOpenDate: value.registrationOpenDate || null,
      registrationCloseDate: value.registrationCloseDate || null,
      location: value.location || undefined,
      meetingLink: value.meetingLink || undefined,
      allowPublicSignup: !!value.allowPublicSignup,
      capacity: value.capacity ?? null,
      notes: value.notes || undefined,
      reminderHoursBefore: value.reminderHoursBefore ?? 24,
      publish: !!value.publish,
      sessions: ((value.sessions ?? []) as EventSessionInput[]).map((session) => ({
        id: session.id ?? null,
        title: session.title || undefined,
        startDate: session.startDate ?? '',
        endDate: session.endDate ?? '',
        location: session.location || undefined,
        meetingLink: session.meetingLink || undefined,
        capacity: session.capacity ?? null,
      })),
    };

    const editingId = this.editingId();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    const call$ = editingId ? this.api.updateEvent(editingId, request) : this.api.createEvent(request);

    call$.pipe(finalize(() => this.busy.set(false))).subscribe({
      next: (detail) => {
        this.message.set(editingId ? 'The event has been updated.' : `Event ${detail.referenceNumber} has been created.`);
        this.showForm.set(false);
        this.editingId.set(null);
        this.load();
      },
      error: (err) => this.error.set(err?.error?.message || 'The event could not be saved.'),
    });
  }

  changeStatus(event: EventSummary, action: EventStatusAction): void {
    this.busy.set(true);
    this.error.set('');
    this.api
      .changeStatus(event.id, action)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.message.set(`${detail.title} is now ${detail.status}.`);
          if (this.selected()?.id === event.id) {
            this.selected.set(detail);
          }
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The event status could not be changed.'),
      });
  }

  // ---- Selected event ----------------------------------------------------

  open(event: EventSummary): void {
    this.busy.set(true);
    this.error.set('');
    this.chosen.set(new Set<number>());
    this.importResult.set(null);
    this.tab.set('registrations');

    this.api
      .getEventForAdmin(event.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.selected.set(detail);
          this.loadRegistrations();
          this.loadAttendance();
          this.loadReport();
        },
        error: () => this.error.set('The event could not be opened.'),
      });
  }

  closeEvent(): void {
    this.selected.set(null);
    this.registrations.set([]);
    this.attendance.set([]);
    this.report.set([]);
  }

  // ---- Registrations -----------------------------------------------------

  loadRegistrations(): void {
    const event = this.selected();
    if (!event) {
      return;
    }

    this.api.getRegistrations(event.id).subscribe({
      next: (registrations) => this.registrations.set(registrations ?? []),
      error: () => this.error.set('The registrations could not be loaded.'),
    });
  }

  toggleChosen(registration: EventRegistrationSummary): void {
    const next = new Set(this.chosen());
    if (next.has(registration.id)) {
      next.delete(registration.id);
    } else {
      next.add(registration.id);
    }
    this.chosen.set(next);
  }

  toggleAll(): void {
    const pending = this.registrations().filter((registration) => registration.status === 'PENDING');
    const allChosen = pending.length > 0 && pending.every((registration) => this.chosen().has(registration.id));
    this.chosen.set(allChosen ? new Set<number>() : new Set(pending.map((registration) => registration.id)));
  }

  decide(registration: EventRegistrationSummary, decision: 'Approve' | 'Reject'): void {
    if (decision === 'Reject' && !this.rejectReason().trim()) {
      this.error.set('A reason is required when rejecting a registration.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.api
      .decideRegistration(registration.id, {
        decision,
        reason: decision === 'Reject' ? this.rejectReason().trim() : null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set(`${registration.fullName ?? 'The attendee'}'s registration has been ${decision === 'Approve' ? 'confirmed' : 'rejected'}.`);
          this.loadRegistrations();
        },
        error: (err) => this.error.set(err?.error?.message || 'The registration could not be decided.'),
      });
  }

  bulkDecide(decision: 'Approve' | 'Reject'): void {
    const ids = [...this.chosen()];
    if (ids.length === 0) {
      this.error.set('Select at least one registration first.');
      return;
    }

    if (decision === 'Reject' && !this.rejectReason().trim()) {
      this.error.set('A reason is required when rejecting registrations.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.api
      .bulkDecideRegistrations(ids, { decision, reason: decision === 'Reject' ? this.rejectReason().trim() : null })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.message.set(`${decision === 'Approve' ? 'Confirmed' : 'Rejected'} ${result.succeeded} of ${result.requested} registration(s).`);
          this.chosen.set(new Set<number>());
          this.loadRegistrations();
        },
        error: (err) => this.error.set(err?.error?.message || 'The registrations could not be decided.'),
      });
  }

  // ---- Attendance --------------------------------------------------------

  loadAttendance(): void {
    const event = this.selected();
    if (!event) {
      return;
    }

    this.api.getAttendance(event.id).subscribe({
      next: (rows) => this.attendance.set(rows ?? []),
      error: () => this.error.set('The attendance sheet could not be loaded.'),
    });
  }

  mark(row: EventAttendanceRow, status: 'PRESENT' | 'ABSENT'): void {
    this.busy.set(true);
    this.error.set('');
    this.api
      .markAttendance(row.registrationId, { status })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set(`${row.fullName ?? 'The attendee'} marked ${status.toLowerCase()}.`);
          this.loadAttendance();
          this.loadReport();
        },
        error: (err) => this.error.set(err?.error?.message || 'The attendance could not be recorded.'),
      });
  }

  onSheetFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const selected = this.selected();
    if (!file || !selected) {
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .uploadAttendanceSheet(selected.id, file)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.importResult.set(result);
          this.message.set(`Attendance recorded: ${result.markedPresent} present, ${result.markedAbsent} absent.`);
          this.loadAttendance();
          this.loadReport();
        },
        error: (err) => this.error.set(err?.error?.message || 'The attendance sheet could not be imported.'),
      });

    input.value = '';
  }

  // ---- Report and correspondence -----------------------------------------

  loadReport(): void {
    const event = this.selected();
    if (!event) {
      return;
    }

    this.api.getReport(event.id).subscribe({
      next: (rows) => this.report.set(rows ?? []),
      error: () => this.report.set([]),
    });
  }

  exportReport(): void {
    const event = this.selected();
    if (!event) {
      return;
    }

    this.busy.set(true);
    this.api
      .exportReport(event.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `event-${event.referenceNumber}-attendees.csv`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: () => this.error.set('The register could not be exported.'),
      });
  }

  sendCorrespondence(): void {
    const event = this.selected();
    if (!event || this.correspondence.invalid) {
      this.correspondence.markAllAsTouched();
      return;
    }

    const value = this.correspondence.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.api
      .sendCorrespondence(event.id, {
        audience: (value.audience ?? 'CONFIRMED') as 'CONFIRMED' | 'ATTENDEES' | 'NO_SHOWS',
        subject: value.subject ?? '',
        body: value.body ?? '',
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (sent) => {
          this.message.set(`Correspondence queued for ${sent} attendee(s).`);
          this.correspondence.reset({ audience: 'CONFIRMED', subject: '', body: '' });
        },
        error: (err) => this.error.set(err?.error?.message || 'The correspondence could not be sent.'),
      });
  }

  // ---- Helpers -----------------------------------------------------------

  private resetForm(detail?: EventDetail): void {
    while (this.sessions.length > 0) {
      this.sessions.removeAt(0);
    }

    (detail?.sessions ?? []).forEach((session) => this.sessions.push(this.buildSession(session)));

    this.form.reset({
      title: detail?.title ?? '',
      description: detail?.description ?? '',
      eventType: (detail?.eventType ?? 'OTHER') as EventType,
      deliveryMode: (detail?.deliveryMode ?? 'IN_PERSON') as EventDeliveryMode,
      targetAudience: (detail?.targetAudience ?? 'PUBLIC') as EventTargetAudience,
      scholarshipId: detail?.scholarshipId ?? null,
      isMandatory: detail?.isMandatory ?? false,
      cycle: detail?.cycle ?? '',
      startDate: this.toLocalInput(detail?.startDate),
      endDate: this.toLocalInput(detail?.endDate),
      registrationOpenDate: this.toLocalInput(detail?.registrationOpenDate),
      registrationCloseDate: this.toLocalInput(detail?.registrationCloseDate),
      location: detail?.location ?? '',
      meetingLink: detail?.meetingLink ?? '',
      allowPublicSignup: detail?.allowPublicSignup ?? false,
      capacity: detail?.capacity ?? null,
      notes: detail?.notes ?? '',
      reminderHoursBefore: detail?.reminderHoursBefore ?? 24,
      publish: detail ? detail.status === 'PUBLISHED' : true,
    });
  }

  private buildSession(session?: {
    id?: number | null;
    title?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    location?: string | null;
    meetingLink?: string | null;
    capacity?: number | null;
  }) {
    return this.fb.group({
      id: [session?.id ?? null],
      title: [session?.title ?? ''],
      startDate: [this.toLocalInput(session?.startDate)],
      endDate: [this.toLocalInput(session?.endDate)],
      location: [session?.location ?? ''],
      meetingLink: [session?.meetingLink ?? ''],
      capacity: [session?.capacity ?? null],
    });
  }

  /** ISO timestamps are shown in a datetime-local input as local wall time. */
  private toLocalInput(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (part: number) => `${part}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  statusClass(status: EventStatus): string {
    switch (status) {
      case 'PUBLISHED':
        return 'status status-approved';
      case 'CLOSED':
        return 'status status-booked';
      case 'CANCELLED':
        return 'status status-rejected';
      default:
        return 'status status-pending';
    }
  }

  registrationClass(status: EventRegistrationStatus): string {
    switch (status) {
      case 'CONFIRMED':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }

  attendanceClass(status?: 'PRESENT' | 'ABSENT' | null): string {
    switch (status) {
      case 'PRESENT':
        return 'status status-approved';
      case 'ABSENT':
        return 'status status-rejected';
      default:
        return 'status status-cancelled';
    }
  }

  audienceLabel(audience: EventTargetAudience): string {
    switch (audience) {
      case 'ALL_AWARDEES':
        return 'All awardees';
      case 'SCHOLARSHIP_AWARDEES':
        return 'Scholarship awardees';
      default:
        return 'Public';
    }
  }

  typeLabel(type: EventType): string {
    return type.charAt(0) + type.slice(1).toLowerCase();
  }

  isPending(registration: EventRegistrationSummary): boolean {
    return registration.status === 'PENDING';
  }

  canPublish(event: EventSummary): boolean {
    return event.status === 'DRAFT';
  }

  canClose(event: EventSummary): boolean {
    return event.status === 'PUBLISHED';
  }
}
